import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';

import { tpoGenKeys } from '../../utils/tpoGenQueryKeys';
import { customerConfigSchema, ConfigValidationError } from '../../config/schema/customerConfigSchema';
import type { CustomerConfigResponse } from '../../types/config';
import { parsePromoConfigResponse } from '../../config/parsePromoConfig';
import type { ParsedPromoOptions } from '../../config/parsePromoConfig';
import { adaptKpiCatalog, adaptPromoKpiCatalog } from '../../config/adapters/kpiCatalogAdapter';
import { parseTpoGenConfig } from '../../config/adapters/parseTpoGenConfig';
import type { TpoGenConfig } from '../../config/adapters/parseTpoGenConfig';
import type { KpiOption } from '../../PlanDetail/MetricPage/config/kpiCatalog';
import { shouldUseFallbackConfig } from '../../config/fallback/shouldUseFallbackConfig';
import { getActiveCustomerSources } from '../../config/customerRegistry';

export interface ParsedCustomerConfig {
    /** Raw, schema-validated backend response. */
    raw: CustomerConfigResponse;
    /** Drop-in replacement for the old static tpoGenConfig object. */
    tpoGenConfig: TpoGenConfig;
    /** Promo type + dependent tactic dropdown options. */
    parsedPromoOptions: ParsedPromoOptions;
    /** Report KPI catalog (Plan Metric view), derived from `metrics[]`. */
    kpiCatalog: KpiOption[];
    /** Report KPI catalog for the Promo Metric view, derived from `promo_metric_grid.requestable_kpis[]`. */
    promoKpiCatalog: KpiOption[];
}

const fetchRawConfig = async (signal?: AbortSignal): Promise<unknown> => {
    // The active customer (REACT_APP_TPO_GEN_CUSTOMER) selects which fe_config
    // module to pair with the response, on both the dev-fallback and
    // real-backend paths. Dynamic imports keep the other customers' fe_config
    // (and, for fallback, its fixture) out of the active bundle.
    const { feConfig, fallbackFixture } = getActiveCustomerSources();

    if (shouldUseFallbackConfig()) {
        const [fixtureMod, feConfigMod] = await Promise.all([fallbackFixture(), feConfig()]);
        return { ...(fixtureMod.default as Record<string, unknown>), fe_config: feConfigMod.default };
    }

    const token = localStorage.getItem('accessToken');
    const [response, feConfigMod] = await Promise.all([
        axios(tpoGenUrls.getConfig, {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
            signal,
        }),
        feConfig(),
    ]);
    return { ...response.data, fe_config: feConfigMod.default };
};

const loadParsedConfig = async (signal?: AbortSignal): Promise<ParsedCustomerConfig> => {
    const rawUnknown = await fetchRawConfig(signal);

    const result = customerConfigSchema.safeParse(rawUnknown);
    if (!result.success) {
        throw new ConfigValidationError(
            result.error.issues.map((issue) => ({ path: issue.path, message: issue.message })),
        );
    }
    const raw = result.data;

    // Adapters run here (not in render) so referential-integrity throws surface
    // as query errors handled by the gate, not the global ErrorBoundary.
    const tpoGenConfig = parseTpoGenConfig(raw);
    const parsedPromoOptions = parsePromoConfigResponse(raw);
    const kpiCatalog = adaptKpiCatalog(raw.metrics);
    const promoKpiCatalog = adaptPromoKpiCatalog(raw.promo_metric_grid?.requestable_kpis, kpiCatalog);

    return { raw, tpoGenConfig, parsedPromoOptions, kpiCatalog, promoKpiCatalog };
};

/**
 * Fetches + validates + parses the customer config once per session.
 * `staleTime`/`gcTime: Infinity` (the global defaults are 1h) keep it cached for
 * the whole SPA session; `retry: false` so a validation error fails fast to the
 * gate instead of retrying.
 */
export function useCustomerConfigQuery() {
    return useQuery({
        queryKey: tpoGenKeys.config(),
        queryFn: ({ signal }) => loadParsedConfig(signal),
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false,
    });
}
